import { useEffect, useMemo, useState } from 'react';

import LoadingView from '@components/atoms/loading-view';
import {
  formatOwnership,
  formatPrice
} from '@lib/page-utils/player-profile/format';
import type { Player } from '@api-service/players';

/**
 * Only the three fields the facts are built from, so callers (and tests) do
 * not have to supply a whole Player.
 */
export type PlayerFactSource = Pick<
  Player,
  'displayName' | 'price' | 'ownership'
>;

const MAX_FACT_COUNT = 10;
const TYPING_INTERVAL_MS = 50;
const FACT_HOLD_MS = 2000;
const PROGRESS_BAR_SECONDS = 9;

const shuffle = <ItemType,>(items: ItemType[]): ItemType[] => {
  const shuffled = [...items];

  for (let index = shuffled.length - 1; index > 0; index--) {
    const swapIndex = Math.floor(Math.random() * (index + 1));
    [shuffled[index], shuffled[swapIndex]] = [
      shuffled[swapIndex],
      shuffled[index]
    ];
  }

  return shuffled;
};

// Odd slots read as ownership so the two fact types alternate. Ownership is
// nullable upstream, so not every odd slot can produce one.
const buildFact = (
  { displayName, price, ownership }: PlayerFactSource,
  slotIndex: number
): string => {
  const isOwnershipSlot = slotIndex % 2 === 1;

  if (isOwnershipSlot && ownership !== null) {
    return `${displayName} is owned by ${formatOwnership(
      ownership
    )} of fantasy managers`;
  }

  return `${displayName} is priced at ${formatPrice(price)}`;
};

const PlayerFactsTypewriter = ({
  players
}: {
  players: PlayerFactSource[];
}) => {
  const [displayText, setDisplayText] = useState('');
  const [currentFactIndex, setCurrentFactIndex] = useState(0);
  const [isTyping, setIsTyping] = useState(true);

  const facts = useMemo(
    () => shuffle(players).slice(0, MAX_FACT_COUNT).map(buildFact),
    [players]
  );

  useEffect(() => {
    if (facts.length === 0) return;

    const currentFact = facts[currentFactIndex % facts.length];
    let characterIndex = 0;
    let advanceTimeout: ReturnType<typeof setTimeout>;

    setDisplayText('');
    setIsTyping(true);

    const typingInterval = setInterval(() => {
      characterIndex += 1;
      setDisplayText(currentFact.slice(0, characterIndex));

      if (characterIndex < currentFact.length) return;

      clearInterval(typingInterval);
      setIsTyping(false);
      advanceTimeout = setTimeout(() => {
        setCurrentFactIndex(
          (previousIndex) => (previousIndex + 1) % facts.length
        );
      }, FACT_HOLD_MS);
    }, TYPING_INTERVAL_MS);

    return () => {
      clearInterval(typingInterval);
      clearTimeout(advanceTimeout);
    };
  }, [currentFactIndex, facts]);

  return (
    <div className="flex flex-col items-center justify-center min-h-[300px] px-4">
      <LoadingView
        progressBarSeconds={PROGRESS_BAR_SECONDS}
        headerText="Preparing your Fantasy Football Hub experience"
        loadingText="Did you know?"
      />
      <div className="text-xl md:text-2xl font-medium text-gray-800 text-center min-h-[60px] flex items-center">
        {displayText}
        {isTyping && <span className="animate-pulse ml-1">|</span>}
      </div>
    </div>
  );
};

export default PlayerFactsTypewriter;
